import React from 'react';
import Head from 'next/head';
import { Row, Col, Pagination } from 'antd';
import directory from '@tutorbot/directory-api';
import { LocationContext } from '../../hoc/Location';
import { LocalizationContext } from '../../hoc/Localization';
import { UserContext } from '../../hoc/User';
import { SearchContext } from '../../hoc/Search';
import { getCourses } from '../../utils/directory';
import { courseClicked, coursesImpressed } from '../../utils/events/courses';
import CourseBlock from '../../components/CourseComponents/HeaderSearch';
import Loading from '../../components/Loading';
import Footer from '../../components/Layout/Footer';
import TopBar from '../../components/Search/TopBar';
import Map from '../../components/Map';
import { getCookie } from '../../utils/cookies';
import styles from './styles.less';

const Search = () => {
  const { location: locat, countries } = React.useContext(LocationContext);
  const { user } = React.useContext(UserContext);
  const { t, translate } = React.useContext(LocalizationContext);
  const {
    filters,
    pushFilters,
    setActiveMarker,
    setHoverMarker,
    hoverMarker,
    activeMarker,
  } = React.useContext(SearchContext);

  const [defaultLocation] = React.useState(locat);
  const [defCategories, setDefCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [mapMode, setMapMode] = React.useState('mobile');
  const [bounds, setBounds] = React.useState(null);
  const [results, setResults] = React.useState({});
  const [options, setOptions] = React.useState({});
  const [location, setLocation] = React.useState(locat);

  const fetchCourses = async page => {
    if (process.browser) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }

    if (filters.course && filters.school) {
      setLoading(true);
      return;
    }

    const modifiedFilters = { ...filters };
    modifiedFilters.countries = countries;
    if (page) {
      modifiedFilters.page = page;
    }
    if (
      !modifiedFilters.search &&
      !modifiedFilters.language &&
      !modifiedFilters.courseType &&
      !modifiedFilters.category &&
      !modifiedFilters.type &&
      !modifiedFilters.school
    ) {
      return;
    }
    if (mapMode === 'map' && modifiedFilters.location) {
      try {
        modifiedFilters.location.bounds = bounds;
      } catch (e) {
        console.error(e);
      }
    } else {
      delete modifiedFilters.location;
    }

    if (mapMode === 'map' && bounds && (!bounds.nw || !bounds.nw.lat)) {
      delete modifiedFilters.location;
    }

    setLoading(true);

    // coursesSearched(modifiedFilters);

    let newResults = { ...results };
    try {
      newResults = await getCourses({
        filters: modifiedFilters,
        page,
        markers: true,
        userTrackingId: getCookie('sync'),
        verified: user && filters.verified || true,
      });
      coursesImpressed(newResults.data, {
        currentPage: newResults.currentPage,
        perPage: newResults.perPage,
      });
    } catch (e) {
      console.error(e);
    }
    setResults(newResults);
  };

  React.useEffect(() => {
    setLoading(false);
  }, [results]);

  const loadOptions = async () => {
    const [defaultCategories, courseTypes = [], languages = [], dayTimes] = await Promise.all([
      directory().categories.all(),
      directory().courseTypes.all(),
      directory().languages.all(),
      directory().dayTimes.all(),
    ]);

    const categories = { '': 'search.category' };
    // eslint-disable-next-line
    for (const category of defaultCategories) {
      categories[category.label] = translate(category, 'name');
    }

    setDefCategories(defaultCategories);
    setOptions({
      courseTypes,
      languages,
      dayTimes,
      levels: [
        { key: 'A1', value: 'Beginner', category: '5d0ba373d59634001216533f' },
        { key: 'A2', value: 'Elementary', category: '5d0ba373d59634001216533f' },
        { key: 'B1', value: 'Intermediate', category: '5d0ba373d59634001216533f' },
        { key: 'B2', value: 'Upper Intermediate', category: '5d0ba373d59634001216533f' },
        { key: 'C1', value: 'Advanced', category: '5d0ba373d59634001216533f' },
        { key: 'C2', value: 'Proficiency', category: '5d0ba373d59634001216533f' },
      ],
      frequency: [
        { title: 'Intensive', value: 'Intensive' },
        { title: 'Standard', value: 'Standard' },
      ],
      categories,
      loaded: true,
    });
  };

  const isMapMode = () => window.innerWidth >= 1199;

  const onResize = () => {
    setMapMode(isMapMode() ? 'map' : 'mobile');
  };

  React.useEffect(() => {
    window.addEventListener('resize', onResize);
    loadOptions();

    return window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    setMapMode(isMapMode() ? 'map' : 'mobile');
  }, [bounds, filters.search]);

  const showActiveMarker = ({ lat, lng }) => {
    setActiveMarker({
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    });
  };

  const resetActiveMarker = () => {
    setActiveMarker(null);
  };

  const showHoverCourses = ({ lat, lng }) => {
    setHoverMarker({
      lat: parseFloat(lat),
      lng: parseFloat(lng),
    });
  };

  const resetHoverCourses = () => {
    setHoverMarker(null);
  };

  const onMapChange = newLocation => {
    const { center: newCenter, bounds: newBounds } = newLocation;

    filters.location = {
      ...newLocation,
    };

    setLocation(filters.location);
    setBounds(newBounds);

    pushFilters({
      ...filters,
      location: {
        center: newCenter,
        zoom: newLocation.zoom,
      },
    });
  };

  React.useEffect(() => {
    if ((bounds && bounds.nw.lat && mapMode === 'map') || mapMode === 'mobile') {
      fetchCourses(+filters.page);
    }
  }, [filters, mapMode, bounds, filters.search]);

  const setCenter = ({ lat, lng }) => {
    filters.location.center = { lat, lng };
    filters.sort = 'near';
    pushFilters(filters);

    setTimeout(() => {
      showHoverCourses({ lat, lng });
      showActiveMarker({ lat, lng });
    }, 250);
  };

  const map =
    mapMode === 'map' ? (
      <Map
        defaultCenter={defaultLocation.center}
        defaultZoom={defaultLocation.zoom}
        center={location.center}
        zoom={location.zoom}
        markers={(results.markers || []).map(marker => ({
          ...marker,
          onMouseEnter: () => showHoverCourses(marker),
          onMouseLeave: () => resetHoverCourses(),
          onClick: () => setCenter(marker),
        }))}
        activeMarker={activeMarker}
        onChange={e => onMapChange(e)}
      />
    ) : null;

  const onPageChange = page => {
    filters.page = page;
    pushFilters(filters);
    fetchCourses(+page);
  };

  return (
    <>
      <Head>
        <title>{`${t('meta.title.search')} | ${t('meta.title.suffix')}`}</title>
        <meta name="description" content={t('meta.description.search')} />
        <meta name="Description" content={t('meta.description.search')} />
      </Head>
      <Row type="flex" className={styles.layout}>
        <Col xs={24} xl={14} xxl={12}>
          {!!loading && (
            <div className={styles.loading}>
              <Loading />
            </div>
          )}
          <div className={styles.list}>
            <div className={styles.inner}>
              <div className={styles.topbarWrapper}>
                <TopBar
                  className={styles.topbar}
                  options={options}
                  defaultCategories={defCategories}
                />
              </div>

              {results.total === 0 && (
                <div id="noResults" className={styles.noResults}>
                  <h2>{t('noresults.header')}</h2>
                  <h3>{t('noresults.text')}</h3>
                </div>
              )}

              {!!results.total && (
                <>
                  <div
                    id="itemList"
                    className={[styles.blocks, hoverMarker ? styles.hover : null].join(' ')}
                  >
                    {results.data.map(course => (
                      <div
                        className={[
                          styles.block,
                          hoverMarker &&
                            parseFloat(course.location.lat) === hoverMarker.lat &&
                            parseFloat(course.location.lng) === hoverMarker.lng
                            ? styles.hover
                            : null,
                        ].join(' ')}
                        onMouseEnter={() => showActiveMarker(course.location)}
                        onMouseLeave={() => resetActiveMarker()}
                        onClick={() => courseClicked(course)}
                        key={course.id}
                      >
                        <CourseBlock course={course} search type="block" />
                      </div>
                    ))}
                  </div>
                  <Pagination
                    className={styles.pagination}
                    current={parseInt(results.currentPage, 10)}
                    onChange={page => onPageChange(page)}
                    pageSize={results.perPage}
                    total={results.total}
                    hideOnSinglePage
                    showSizeChanger={false}
                  />
                </>
              )}
            </div>
            <Footer />
          </div>
        </Col>
        <Col xs={0} xl={10} xxl={12}>
          <div className={styles.map}>{map}</div>
        </Col>
      </Row>
    </>
  );
};

export default Search;
